/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import {
    CONTROL_SIZE,
    ErrorMessage,
    FormLayout,
    Icon,
    INDENT,
    LoginBackIcon,
    Notification,
    NOTIFICATION_STATUS,
    Spinner,
    STACK_ALIGN,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
} from '@veeam/components';

import { FormValidator, immediatelyValidateFlag, useExternalFormApi, Form, TextInput } from '../../../form';
import { useAutoFocus } from '../../../../hooks/use-auto-focus';
import { FormContainer, LOGIN_FORM_WIDTH } from '../FormContainer';
import { LoginButton, StyledLinkButton } from '../LoginButtons';
import { LoginTitle } from '../LoginTitle';
import { FieldLengths } from '../../../../enums';

import type { ResetPasswordFormState } from '../../interfaces';

const defaultTexts: Required<ResetPasswordFormProps['texts']> = {
    labelUsername: 'Username',
    labelEmail: 'Email',
    actionLogIn: 'Reset',
    returnButtonText: 'Back',
    errorRequired: 'The field is required',
    errorMaxLength: 'The maximum length is {0}',
    warningAboutReset: 'After you press "Reset" you will be sent a letter with a link for creating a new password. Please check your mailbox.',
    placeholderUserName: 'Enter Company\\User',
    placeholderEmail: 'Enter email',
};

const usernameInputName = 'userName';
const emailInputName = 'email';
export const resetPasswordFormInitialState: ResetPasswordFormState = {
    [usernameInputName]: '',
    [emailInputName]: '',
};

export interface ResetPasswordFormProps {
    title?: string;
    onReturnButtonClick?: () => void;
    loading?: boolean;
    error?: string;
    texts?: {
        labelUsername?: string;
        labelEmail?: string;
        actionLogIn?: string;
        returnButtonText?: string;
        errorRequired?: string;
        errorMaxLength?: string;
        warningAboutReset?: string;
        placeholderUserName?: string;
        placeholderEmail?: string;
    };
    logIn: (value: ResetPasswordFormState) => void;
    onChange?: (value: any) => void;
    customUsernameValidation?: (value: string) => string | void;
    customEmailValidation?: (value: string) => string | void;
    usernameLength?: number;
    emailLength?: number;
    formWidth?: string;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = (props) => {
    const {
        title = 'Verification',
        texts: customTexts,
        loading,
        error,
        logIn,
        onReturnButtonClick,
        onChange,
        customUsernameValidation,
        customEmailValidation,
        usernameLength = FieldLengths.UsernameLength,
        emailLength = FieldLengths.UsernameLength,
        formWidth = `${LOGIN_FORM_WIDTH}px`,
    } = props;
    const texts = { ...defaultTexts, ...customTexts };
    const usernameInputRef = useAutoFocus();
    const emailInputRef = useRef<HTMLInputElement | null>(null);
    const formApi = useExternalFormApi<ResetPasswordFormState>();
    const stepValidate = (data: ResetPasswordFormState) => {
        const validator = new FormValidator(data);

        validator.validate(usernameInputName)
            .string()
            .required(texts.errorRequired)
            .maxLength(usernameLength, `${texts.errorMaxLength}${immediatelyValidateFlag}`)
            .check((value: string) => {
                if (customUsernameValidation) {
                    return customUsernameValidation(value);
                }
            });
        validator.validate(emailInputName)
            .string()
            .required(texts.errorRequired)
            .maxLength(emailLength, `${texts.errorMaxLength}${immediatelyValidateFlag}`)
            .check((value: string) => {
                if (customEmailValidation) {
                    return customEmailValidation(value);
                }
            });

        return validator.result();
    };

    return (
        <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.s}>
            <FormContainer direction={STACK_DIRECTION.column} gap={STACK_GAP.s} align={STACK_ALIGN.end}>
                <StackView align={STACK_ALIGN.start} direction={STACK_DIRECTION.column} gap={STACK_GAP.s} size={{ width: formWidth }}>
                    {onReturnButtonClick && (
                        <StyledLinkButton
                            compact
                            disabled={loading}
                            onClick={onReturnButtonClick}
                            iconBefore={<Icon src={<LoginBackIcon />} />}
                        >
                            {texts.returnButtonText}
                        </StyledLinkButton>
                    )}

                    <LoginTitle text={title} />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </StackView>

                <Form
                    value={resetPasswordFormInitialState}
                    validate={stepValidate}
                    onChange={onChange}
                    externalFormApi={formApi}
                    style={{ width: '100%' }}
                    onSubmit={logIn}
                    fields={[
                        { name: usernameInputName, ref: usernameInputRef },
                        { name: emailInputName, ref: emailInputRef },
                    ]}
                >
                    <StackView
                        direction={STACK_DIRECTION.column}
                        gap={STACK_GAP.s}
                        align={STACK_ALIGN.start}
                        spaceTop={error ? INDENT.none : INDENT.m}
                    >
                        <FormLayout disabled={loading} inlineLabel>
                            <TextInput
                                data-tid={`${ResetPasswordForm.name}_${usernameInputName}`}
                                name={usernameInputName}
                                controlRef={usernameInputRef}
                                label={texts.labelUsername}
                                size={CONTROL_SIZE.full}
                                inlineLabel
                                placeholder={texts.placeholderUserName}
                            />

                            <TextInput
                                data-tid={`${ResetPasswordForm.name}_${emailInputName}`}
                                name={emailInputName}
                                controlRef={emailInputRef}
                                label={texts.labelEmail}
                                size={CONTROL_SIZE.full}
                                inlineLabel
                                placeholder={texts.placeholderEmail}
                            />
                        </FormLayout>
                    </StackView>
                </Form>

                <StackView direction={STACK_DIRECTION.column} align={STACK_ALIGN.start} size={{ width: formWidth }}>
                    <Notification status={NOTIFICATION_STATUS.info}>{texts.warningAboutReset}</Notification>
                </StackView>

                <StackView
                    direction={STACK_DIRECTION.row}
                    spaceTop={INDENT.s}
                    align={STACK_ALIGN.start}
                    size={{ width: formWidth }}
                >
                    <LoginButton onClick={() => formApi.submit()} disabled={loading}>
                        {texts.actionLogIn}
                    </LoginButton>

                    {loading && <Spinner />}
                </StackView>
            </FormContainer>
        </StackView>
    );
};
